<template>
  <AppResizableContainer layout="vertical">
    <template #headerContent>
      <div class="grid grid-cols-[7fr_5fr] divide-x-2 divide-gray-200 h-full">
        <div class="flex flex-col">
          <div class="p-1.5 text-sm font-semibold">{{ $t('pricing-catalogs.orderPolicies') }}</div>
          <div class="flex-1 overflow-hidden">
            <PriceCatalogsOrderListHeaders
              :mode="mode"
              :selected-row-id="priceCatalogOrder?.id ?? ''"
              :price-catalog-order-items="priceCatalogOrderItems"
              @item-selected="onHeaderSelected"
              @modified-header="onHeaderModified"
              @added-header="onHeaderAdded"
              @refresh="loadData()" />
          </div>
        </div>

        <div class="flex flex-col">
          <div class="p-1.5 text-sm font-semibold">{{ $t('pricing-catalogs.priceCatalogs') }}</div>
          <div class="flex-1 overflow-hidden">
            <PriceCatalogsOrderListDetails
              :mode="mode"
              :price-catalogs-list="priceCatalogsList"
              :price-catalog-order="priceCatalogOrder"
              :price-catalog-order-details="priceCatalogOrderDetails"
              @detail-added="onDetailAdded"
              @detail-deleted="onDetailDeleted"
              @order-changed="onOrderChanged" />
          </div>
        </div>
      </div>
    </template>
    <template #detailsContent>
      <PriceCatalogsOrderListCustomersTable
        v-if="priceCatalogOrder"
        :mode="mode"
        :selected-page="customersForPolicySelectedPage"
        :table-data="customersForPolicyTableData"
        :table-data-count="customersForPolicyTableDataTotalCount"
        :added-customers="customersAddedInAnyPolicy"
        @add-customers="onAddCustomers"
        @delete-customers="onDeleteCustomers"
        @refresh-table-data="onRefreshCustomersTableData"
        @add-all-customers="onAddAllCustomers" />
    </template>
  </AppResizableContainer>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToastStore } from '@/stores/toast';
import { deepCopy } from '@/utils/data';

import PriceCatalogsOrderListHeaders from '../prices/PriceCatalogsOrderListHeaders.vue';
import PriceCatalogsOrderListDetails from '../prices/PriceCatalogsOrderListDetails.vue';
import AppResizableContainer from '../common/AppResizableContainer.vue';
import PriceCatalogsOrderListCustomersTable from './PriceCatalogsOrderListCustomersTable.vue';

import {
  type PricingPolicyCatalogOrderHeader,
  type PricingPoliciesCatalogOrderDetail,
  type PriceCatalog,
} from '@/types/PricingTypes';

import {
  getPriceCatalogs,
  getPricingPolicyCatalogsOrder,
  removePricingPolicyCatalogsOrderByIds,
  addOrEditPricingPolicyCatalogsOrder,
  getPricingPolicyCatalogsOrderDetails,
  addOrEditPricingPolicyCatalogOrderDetails,
  removePricingPolicyCatalogOrderDetailsByIds,
  addAllCustomersToPricePolicy,
} from '@/services/api/prices';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { Customer } from '@/types/Traders';
import { getCustomersWithConditions } from '@/services/api/traders';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();

const priceCatalogOrderItems = ref<PricingPolicyCatalogOrderHeader[]>([]);
const priceCatalogOrder = ref<PricingPolicyCatalogOrderHeader>();

const originalPriceCatalogOrderItems = ref<PricingPolicyCatalogOrderHeader[]>([]);

const priceCatalogOrderDetails = ref<PricingPoliciesCatalogOrderDetail[]>([]);
const originalCatalogOrderDetails = ref<PricingPoliciesCatalogOrderDetail[]>([]);
const priceCatalogsList = ref<PriceCatalog[]>([]);

defineProps<{
  mode: 'view' | 'edit';
}>();

const emit = defineEmits(['addAll']);

onMounted(async () => {
  await loadData();
  if (priceCatalogOrderItems.value.length) await onHeaderSelected(priceCatalogOrderItems.value[0]);
});

async function loadData() {
  await loadHeaders();
  await loadCatalogs();
  await loadDetails();
  await loadCustomersForPricePolicy({ page: 1, itemsPerPage: 10 });
}

async function loadHeaders() {
  let response = await getPricingPolicyCatalogsOrder();
  if (!response) return;

  priceCatalogOrderItems.value = response;
  originalPriceCatalogOrderItems.value = deepCopy<PricingPolicyCatalogOrderHeader[]>(
    priceCatalogOrderItems.value
  );
}

async function loadCatalogs() {
  priceCatalogsList.value = (await getPriceCatalogs()) ?? [];
}

async function loadDetails() {
  let response = await getPricingPolicyCatalogsOrderDetails();
  if (!response) return;
  priceCatalogOrderDetails.value = response;
  originalCatalogOrderDetails.value = deepCopy<PricingPoliciesCatalogOrderDetail[]>(
    priceCatalogOrderDetails.value
  );
}

async function onHeaderSelected(header: PricingPolicyCatalogOrderHeader) {
  priceCatalogOrder.value = header;
  // Trigger reactivity by assigning new references
  priceCatalogOrderDetails.value = [...priceCatalogOrderDetails.value];
  await loadCustomersForPricePolicy({
    page: 1,
    itemsPerPage: customersForPolicyItemsPerPage.value,
  });
}

function onHeaderAdded(header: PricingPolicyCatalogOrderHeader) {
  priceCatalogOrderItems.value.push({ ...header, recordMode: RecordMode.Added });

  // Trigger reactivity by assigning new references
  priceCatalogOrderItems.value = [...priceCatalogOrderItems.value];
}

function onHeaderModified(header: PricingPolicyCatalogOrderHeader) {
  const hasActiveDetails = priceCatalogOrderDetails.value.some(
    (x) => x.pricingPolicyCatalogOrderHeaderId == header.id && !x.deleted
  );
  const index = priceCatalogOrderItems.value.findIndex((x) => x.id === header.id);
  if (index <= -1) return;
  if (header.deleted) {
    // if the header was Added, ergo not on the server, remove immediately
    if (header.recordMode == RecordMode.Added) {
      priceCatalogOrderItems.value.splice(index, 1);
      return;
    }
    // If attempting to delete but there are active details, disallow deletion
    if (hasActiveDetails) {
      header.deleted = false;
      toastStore.attention({
        hideTitle: true,
        text: 'Cannot delete a policy that has active details.',
      });
      return;
    } else header.recordMode = RecordMode.Deleted;
  } else header.recordMode = RecordMode.Modified;

  // Update list
  priceCatalogOrderItems.value[index] = { ...priceCatalogOrderItems.value[index], ...header };

  // Trigger reactivity by assigning new references
  priceCatalogOrderItems.value = [...priceCatalogOrderItems.value];
  if (priceCatalogOrder.value?.id == header.id)
    priceCatalogOrder.value = priceCatalogOrderItems.value[index];
}

function onDetailAdded(detail: PricingPoliciesCatalogOrderDetail) {
  priceCatalogOrderDetails.value.push({
    ...detail,
    recordMode: RecordMode.Added,
    sequence: priceCatalogOrderDetails.value.length,
  });

  // Trigger reactivity by assigning new references
  priceCatalogOrderDetails.value = [...priceCatalogOrderDetails.value];
}

function onDetailDeleted(detail: PricingPoliciesCatalogOrderDetail) {
  const index = priceCatalogOrderDetails.value.findIndex((x) => x.id == detail.id);
  if (index > -1) {
    if (detail.recordMode == RecordMode.Added) priceCatalogOrderDetails.value.splice(index, 1);
    else
      priceCatalogOrderDetails.value[index] = {
        ...priceCatalogOrderDetails.value[index],
        recordMode: RecordMode.Deleted,
        deleted: true,
      };
  }

  // Trigger reactivity by assigning new references
  priceCatalogOrderDetails.value = [...priceCatalogOrderDetails.value];
}

function onOrderChanged(details: PricingPoliciesCatalogOrderDetail[]) {
  details.forEach((detail) => {
    const index = priceCatalogOrderDetails.value.findIndex((x) => x.id == detail.id);
    if (index <= -1) return;

    const newSequence = details.findIndex((x) => x.id == detail.id);
    if (detail.recordMode != RecordMode.Added) detail.recordMode = RecordMode.Modified;
    priceCatalogOrderDetails.value[index] = {
      ...priceCatalogOrderDetails.value[index],
      sequence: newSequence,
      recordMode: detail.recordMode,
    };
  });

  // Trigger reactivity by assigning new references
  priceCatalogOrderDetails.value = [...priceCatalogOrderDetails.value];
}

async function onCancel() {
  await loadData();
}

async function onSave() {
  await savePriceCatalogOrderHeaders();
  await savePriceCatalogOrderDetails();
  await loadData();
}

async function savePriceCatalogOrderHeaders() {
  const headersToAddOrUpdate = priceCatalogOrderItems.value.filter(
    (x) => x.recordMode == RecordMode.Added || x.recordMode == RecordMode.Modified
  );
  if (headersToAddOrUpdate.some((x) => x))
    await addOrEditPricingPolicyCatalogsOrder(headersToAddOrUpdate);

  const headerIdsToDelete = priceCatalogOrderItems.value
    .filter((x) => x.recordMode == RecordMode.Deleted)
    .map((x) => x.id);
  if (headerIdsToDelete.some((x) => x))
    await removePricingPolicyCatalogsOrderByIds(headerIdsToDelete);
}

async function savePriceCatalogOrderDetails() {
  const detailsToAddOrUpdate = priceCatalogOrderDetails.value.filter(
    (x) => x.recordMode == RecordMode.Added || x.recordMode == RecordMode.Modified
  );
  if (detailsToAddOrUpdate.some((x) => x))
    await addOrEditPricingPolicyCatalogOrderDetails(detailsToAddOrUpdate);

  const detailIdsToDelete = priceCatalogOrderDetails.value
    .filter((x) => x.recordMode == RecordMode.Deleted)
    .map((x) => x.id);
  if (detailIdsToDelete.some((x) => x))
    await removePricingPolicyCatalogOrderDetailsByIds(detailIdsToDelete);
}

const customersForPolicyTableData = ref<Customer[]>([]);
const customersForPolicyTableDataTotalCount = ref<number>(0);
const customersForPolicySelectedPage = ref(1);
const customersForPolicyItemsPerPage = ref(10);

async function loadCustomersForPricePolicy(data: TableFilterData) {
  if (!priceCatalogOrder.value) return;
  const currentPage = data.page ?? 1;
  customersForPolicyItemsPerPage.value = data.itemsPerPage ?? 10;

  const activePriceCatalogOrder = priceCatalogOrderItems.value.find(
    (p) => p.id === priceCatalogOrder.value?.id
  );
  if (!activePriceCatalogOrder) return;
  let addedCustomers: Customer[] = [];
  if (activePriceCatalogOrder.modifiedCustomers)
    addedCustomers = activePriceCatalogOrder.modifiedCustomers?.filter(
      (x) => x.recordMode === RecordMode.Added
    );

  let serverCustomers: Customer[] = [];
  let serverCustomersCount: number = 0;

  const res = await getCustomersWithConditions({
    ...data,
    page: currentPage,
    itemsPerPage: customersForPolicyItemsPerPage.value,
    pricingPolicyId: priceCatalogOrder.value.pricingPolicyId,
  });
  if (res) {
    serverCustomers = res.data;
    serverCustomersCount = res.count;
  }
  const serverPages = Math.ceil(serverCustomersCount / customersForPolicyItemsPerPage.value);
  const serverPageItemCount = serverCustomers.length;
  const pageAvailableSpace = Math.max(
    0,
    customersForPolicyItemsPerPage.value - serverPageItemCount
  );

  if (currentPage < serverPages) {
    // get page from server
    customersForPolicyTableData.value = serverCustomers;
  } else if (currentPage > serverPages) {
    // Get page from local data
    const mod = serverCustomersCount % customersForPolicyItemsPerPage.value;
    const availableSpace = mod === 0 ? 0 : customersForPolicyItemsPerPage.value - mod;
    const modifiedCurrentPage = currentPage - serverPages;
    const startIndex =
      (modifiedCurrentPage - 1) * customersForPolicyItemsPerPage.value + availableSpace;
    const endIndex = startIndex + customersForPolicyItemsPerPage.value;
    customersForPolicyTableData.value = addedCustomers.slice(startIndex, endIndex);
  } else {
    // Get boundary page (last server + fill with modified)
    if (pageAvailableSpace > 0) {
      const modifiedData = addedCustomers.slice(0, pageAvailableSpace);
      customersForPolicyTableData.value = [...serverCustomers, ...modifiedData];
    } else {
      customersForPolicyTableData.value = serverCustomers;
    }
  }

  customersForPolicyTableDataTotalCount.value = serverCustomersCount + addedCustomers.length;

  let deletedCustomers: Customer[] = [];
  if (activePriceCatalogOrder.modifiedCustomers)
    deletedCustomers = activePriceCatalogOrder.modifiedCustomers.filter(
      (x) => x.recordMode === RecordMode.Deleted
    );

  if (deletedCustomers.length) {
    deletedCustomers.forEach((deleted) => {
      const existing = customersForPolicyTableData.value.find((x) => x.id === deleted.id);
      if (existing) existing.deleted = true;
    });
  }
}

const customersAddedInAnyPolicy = ref<Customer[]>([]);
async function onAddCustomers(customers: Customer[]) {
  if (!priceCatalogOrder.value) return;
  const activePriceCatalogOrder = priceCatalogOrderItems.value.find(
    (p) => p.id === priceCatalogOrder.value?.id
  );
  if (!activePriceCatalogOrder) return;

  if (activePriceCatalogOrder.recordMode == RecordMode.Deleted) {
    toastStore.attention({
      hideTitle: true,
      text: $t('messages.noUpdateDeleted'),
    });
    return;
  }

  if (!activePriceCatalogOrder.modifiedCustomers) activePriceCatalogOrder.modifiedCustomers = [];

  if (activePriceCatalogOrder.recordMode !== RecordMode.Added)
    activePriceCatalogOrder.recordMode = RecordMode.Modified;

  activePriceCatalogOrder.modifiedCustomers.push(...customers);
  customersAddedInAnyPolicy.value.push(...customers);

  // This will trigger load table data from refreshing table to last page
  const lastPage = Math.ceil(
    customersForPolicyTableDataTotalCount.value / customersForPolicyItemsPerPage.value
  );
  if (customersForPolicySelectedPage.value === lastPage)
    await loadCustomersForPricePolicy({
      page: lastPage,
      itemsPerPage: customersForPolicyItemsPerPage.value,
    });
  else customersForPolicySelectedPage.value = lastPage;
}

function onDeleteCustomers(customers: Customer[]) {
  const activePriceCatalogOrder = priceCatalogOrderItems.value.find(
    (p) => p.id === priceCatalogOrder.value?.id
  );
  if (!activePriceCatalogOrder) return;

  if (activePriceCatalogOrder.recordMode == RecordMode.Deleted) {
    toastStore.attention({
      hideTitle: true,
      text: $t('messages.noUpdateDeleted'),
    });
    return;
  }

  customers.forEach((customer) => {
    if (!activePriceCatalogOrder.modifiedCustomers) activePriceCatalogOrder.modifiedCustomers = [];
    const existingIndexInModified = activePriceCatalogOrder.modifiedCustomers.findIndex(
      (m) => m.id === customer.id
    );
    if (existingIndexInModified !== -1) {
      // Customer exists in modified as "Added"
      const existingInModified = activePriceCatalogOrder.modifiedCustomers[existingIndexInModified];
      if (existingInModified.recordMode === RecordMode.Added) {
        activePriceCatalogOrder.modifiedCustomers.splice(existingIndexInModified, 1);
      }
      // Remove from ui table
      const existingIndexInTable = customersForPolicyTableData.value.findIndex(
        (c) => c.id === customer.id
      );
      if (existingIndexInTable !== -1)
        customersForPolicyTableData.value.splice(existingIndexInTable, 1);
      // Remove from added in all policies to show in "AddCustomers" modal
      const existingInAll = customersAddedInAnyPolicy.value.findIndex((c) => c.id == customer.id);
      if (existingInAll !== -1) customersAddedInAnyPolicy.value.splice(existingInAll, 1);
    } else {
      const existing = customersForPolicyTableData.value.find((x) => x.id == customer.id);
      if (existing) existing.deleted = true;
      activePriceCatalogOrder.modifiedCustomers.push(customer);
      if (activePriceCatalogOrder.recordMode !== RecordMode.Added)
        activePriceCatalogOrder.recordMode = RecordMode.Modified;
    }
  });
}

function onRefreshCustomersTableData(data: TableFilterData) {
  loadCustomersForPricePolicy(data);
}

async function onAddAllCustomers() {
  if (!priceCatalogOrder.value) return;
  await addAllCustomersToPricePolicy(priceCatalogOrder.value.pricingPolicyId);
  await onSave();
  emit('addAll');
}

const isModified = ref<boolean>(false);

watchEffect(() => {
  isModified.value =
    JSON.stringify(priceCatalogOrderItems.value) !==
      JSON.stringify(originalPriceCatalogOrderItems.value) ||
    JSON.stringify(priceCatalogOrderDetails.value) !==
      JSON.stringify(originalCatalogOrderDetails.value);
});

defineExpose({
  onSave,
  onCancel,
  isModified,
});
</script>
